import {
  SuggestedActionDto,
  SuggestedNeedDto,
  SuggestedScheduledEventDto,
  SuggestionsDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import {
  Button,
  FlexContainer,
  IconLibrary,
  ReemaThemeV2,
} from '@act/storybook';
import { Box, Typography } from '@mui/material';
import { metaSliceSelectors } from 'redux/redux';

interface Props {
  suggestions: SuggestionsDto;
  onSaveClick: () => void;
  member: UserDto;
  setOpenAddSuggestedNeed: (need: SuggestedNeedDto | null) => void;
  setOpenAddAction: (action: SuggestedActionDto | null) => void;
  setOpenAddScheduledEvent: (event: SuggestedScheduledEventDto | null) => void;
}

interface SuggestionCardProps {
  id?: string;
  type: 'need' | 'action' | 'scheduled event';
  title: string;
  description?: string;
  onClick: () => void;
  shortTitle?: string;
}

type SuggestionItem = {
  id?: string;
  key: string;
  type: 'need' | 'action' | 'scheduled event';
  title: string;
  description?: string;
  shortTitle?: string;
};

const SuggestionCard = ({
  type,
  title,
  description,
  onClick,
  shortTitle,
}: SuggestionCardProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[100],
        border: `1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`,
        borderRadius: '12px',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            sx={{ alignSelf: 'center' }}
            variant="titleMedium"
            fontWeight={600}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Button
              variant="text"
              onClick={onClick}
              startIcon={<IconLibrary iconName="Plus" weight="regular" />}
            >
              Add {type}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 1 }}>
          {shortTitle && (
            <Typography
              variant="bodySmall"
              sx={{
                color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[800],
              }}
            >
              {shortTitle}
            </Typography>
          )}
        </Box>
        {description && (
          <Typography
            variant="bodyMedium"
            sx={{
              color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[600],
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const LlmSuggestions = ({
  suggestions,
  setOpenAddSuggestedNeed,
  setOpenAddAction,
  setOpenAddScheduledEvent,
}: Props) => {
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();

  if (!suggestions) return null;

  const allSuggestions: (SuggestionItem & {
    originalSuggestion:
      | SuggestedNeedDto
      | SuggestedActionDto
      | SuggestedScheduledEventDto;
  })[] = [
    // Needs
    ...suggestions.needs.map((need: SuggestedNeedDto) => {
      const issueLabel = need?.issueId
        ? needMetadata.data.issues.find(i => i.id === need.issueId)?.label
        : undefined;
      return {
        id: need.id,
        key: need.id || need.tempNeedId,
        type: 'need' as const,
        title: issueLabel || 'Suggested Need',
        shortTitle: 'New need discussed',
        description: need?.reason,
        originalSuggestion: need,
      };
    }),
    // Actions
    ...suggestions.actions.map((action: SuggestedActionDto) => {
      const issueLabel = action.need?.issueId
        ? needMetadata.data.issues.find(i => i.id === action.need?.issueId)
            ?.label
        : undefined;
      const activityLabel = action?.activityId
        ? needMetadata.data.activities.find(a => a.id === action?.activityId)
            ?.label
        : undefined;
      return {
        id: action.id,
        key: action.id || `action-${action.activityId}`,
        type: 'action' as const,
        title: issueLabel,
        shortTitle: activityLabel,
        description: action.description,
        originalSuggestion: action,
      };
    }),
    // Scheduled Events
    ...suggestions.scheduledEvents.map((event: SuggestedScheduledEventDto) => ({
      id: event.id,
      key: event.id || `event-${event?.type}`,
      type: 'scheduled event' as const,
      title: `Schedule ${event?.type}`,
      description: event.note,
      originalSuggestion: event,
    })),
  ];

  if (allSuggestions.length === 0) {
    return (
      <>
        <FlexContainer
          borderRadius="lessRounded"
          direction="column"
          gap={4}
          padding={2}
          width="100%"
          height={'100%'}
          backgroundColor={ReemaThemeV2.LIGHT_MODE.palette.greyShades[100]}
          border={`1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`}
        >
          <Typography variant="titleLarge" fontWeight={600}>
            Suggested Actions
          </Typography>
          <Typography
            variant="bodyMedium"
            sx={{ color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[600] }}
          >
            No suggestions available
          </Typography>
        </FlexContainer>
      </>
    );
  }
  const handleClick = (
    type: 'need' | 'action' | 'scheduled event',
    suggestion:
      | SuggestedNeedDto
      | SuggestedActionDto
      | SuggestedScheduledEventDto
  ) => {
    if (type === 'need') {
      setOpenAddSuggestedNeed(suggestion as SuggestedNeedDto);
    } else if (type === 'action') {
      setOpenAddAction(suggestion as SuggestedActionDto);
    } else if (type === 'scheduled event') {
      setOpenAddScheduledEvent(suggestion as SuggestedScheduledEventDto);
    }
  };
  return (
    <>
      <FlexContainer
        borderRadius="lessRounded"
        direction="column"
        gap={4}
        padding={2}
        width="100%"
        height={'100%'}
        backgroundColor={ReemaThemeV2.LIGHT_MODE.palette.greyShades[100]}
        border={`1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`}
      >
        <Typography variant="titleLarge" fontWeight={600}>
          Suggested Actions
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {allSuggestions.map(suggestion => (
            <SuggestionCard
              key={suggestion.key}
              id={suggestion.id}
              type={suggestion.type}
              title={suggestion.title}
              description={suggestion.description}
              shortTitle={suggestion.shortTitle}
              onClick={() =>
                handleClick(suggestion.type, suggestion.originalSuggestion)
              }
            />
          ))}
        </Box>
      </FlexContainer>
    </>
  );
};
